import { ActiveSelection, CanvasEvents } from 'fabric';
import { useEffect } from 'react';

import moveMediaElementsByOperation from 'editor/src/store/design/operation/moveMediaElementsByOperation';
import getMovableMediaElements from 'editor/src/store/design/selector/getMovableMediaElements';
import { useDispatch, useStore } from 'editor/src/store/hooks';

import CustomFabricObject from 'editor/src/fabric/CustomFabricObject';
import useFabricCanvas from 'editor/src/util/useFabricCanvas';
import useFabricUtils from 'editor/src/util/useFabricUtils';

import { CanvasRotation } from 'editor/src/component/EditorArea/types';
import {
  getActiveSelection,
  getSelectionMoveDeltaMm,
  getSelectionUuids,
} from 'editor/src/component/EditorArea/utils/groupSelectionUtils';

// Lets a multi-selection be dragged; the new positions go to the store when the drag ends.
function useMoveGroupSelection(canvasRotation: CanvasRotation) {
  const fabricCanvas = useFabricCanvas();
  const store = useStore();
  const dispatch = useDispatch();
  const { px2mm } = useFabricUtils();

  useEffect(() => {
    const syncMovability = () => {
      const activeSelection = getActiveSelection(fabricCanvas);
      if (!activeSelection) {
        return;
      }
      const uuids = getSelectionUuids(activeSelection.getObjects() as CustomFabricObject[]);
      const locked = !getMovableMediaElements(store.getState(), uuids);
      // A non-evented selection lets the clicks reach the elements underneath.
      activeSelection.set({ evented: !locked, lockMovementX: locked, lockMovementY: locked });
    };

    const onModified = ({ target, transform }: CanvasEvents['object:modified']) => {
      if (!(target instanceof ActiveSelection) || transform?.action !== 'drag') {
        return;
      }
      const { original } = transform;
      const deltaPx = { left: target.left - original.left, top: target.top - original.top };
      if (!deltaPx.left && !deltaPx.top) {
        return;
      }

      // The elements re-render at their new store position and the selection is rebuilt around them.
      target.set({ left: original.left, top: original.top });
      target.setCoords();

      const uuids = getSelectionUuids(target.getObjects() as CustomFabricObject[]);
      const delta = getSelectionMoveDeltaMm(deltaPx, canvasRotation, px2mm);
      dispatch(moveMediaElementsByOperation(uuids, delta.left, delta.top));
      fabricCanvas.requestRenderAll();
    };

    fabricCanvas.on('selection:created', syncMovability);
    fabricCanvas.on('selection:updated', syncMovability);
    fabricCanvas.on('mouse:down:before', syncMovability);
    fabricCanvas.on('object:modified', onModified);
    return () => {
      fabricCanvas.off('selection:created', syncMovability);
      fabricCanvas.off('selection:updated', syncMovability);
      fabricCanvas.off('mouse:down:before', syncMovability);
      fabricCanvas.off('object:modified', onModified);
    };
  }, [fabricCanvas, canvasRotation, px2mm]);
}

export default useMoveGroupSelection;
